import CompactLogo from '@components/atoms/logo/CompactLogo';
import { HorizontalLogo } from '@components/atoms/logo/horizontalLogo';
import { SquareIcon, CheckIcon } from 'bda-web-ui-components';
import {
  cloneElement,
  forwardRef,
  type FC,
  type HTMLAttributes,
  type PropsWithChildren,
  type ReactElement,
  type SVGProps,
} from 'react';

function cx(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(' ');
}

type StepStatus = 'done' | 'active' | 'pending';

const itemBoxClassesByStatus: Record<StepStatus, string> = {
  done: 'border-2 bg-white/30 border-success-400 bg-success-300',
  active:
    'border-2 bg-white/30 border-1 border-white shadow-[0px_0px_0px_2px_rgba(255,255,255,0.5),_0px_0px_0px_5px_rgba(255,255,255,0.5)]',
  pending: 'border-2 bg-white/30 border-0',
};

const colorTokensByStatus: Record<
  StepStatus,
  { icon: string; separator: string; text: string }
> = {
  done: {
    icon: 'fill-success-400 text-success-400 stroke-success-400',
    separator: 'bg-success-400',
    text: 'text-white',
  },
  active: {
    icon: 'fill-white',
    separator: 'bg-white',
    text: 'text-white',
  },
  pending: {
    icon: 'fill-red-300',
    separator: 'bg-red-300',
    text: 'text-red-300',
  },
};

export interface StepperItem {
  id: string;
  icon: ReactElement<SVGProps<SVGSVGElement>>;
  text: string;
}

interface SideStepperItemProps extends HTMLAttributes<HTMLDivElement> {
  status?: StepStatus;
  text: string;
  icon: ReactElement<SVGProps<SVGSVGElement>>;
  showSeparator?: boolean;
  collapsed?: boolean;
}

const SideStepperItem: FC<SideStepperItemProps> = ({
  status = 'pending',
  text,
  icon,
  showSeparator = true,
  collapsed = false,
}) => {
  const tokens = colorTokensByStatus[status];
  const baseIcon = status === 'done' ? <CheckIcon className="w-4 h-4" /> : icon;
  const coloredIcon = cloneElement(baseIcon, {
    className: cx(baseIcon.props?.className, tokens.icon),
  });

  return (
    <div className="h-[60px] relative pl-12 pb-[30px]">
      <div
        className={cx(
          'absolute top-0 h-full',
          collapsed ? 'left-2.5' : 'left-0',
        )}
      >
        <SquareIcon
          className={cx(
            itemBoxClassesByStatus[status],
            'rounded-lg p-2 h-8 w-8',
          )}
        >
          {coloredIcon}
        </SquareIcon>
        {showSeparator && (
          <div
            className={cx('w-0.5 h-[28px] ml-auto mr-auto', tokens.separator)}
          />
        )}
      </div>
      {!collapsed && (
        <span
          className={cx(
            'text-base flex items-center justify-start font-bold h-[30px]',
            tokens.text,
          )}
        >
          {text}
        </span>
      )}
    </div>
  );
};

export interface SideStepperProps
  extends HTMLAttributes<HTMLDivElement>,
    PropsWithChildren {
  items: StepperItem[];
  activeId: string;
  completed: boolean;
  title: string;
  collapsed?: boolean;
}

export const SideStepper = forwardRef<HTMLDivElement, SideStepperProps>(
  (
    {
      activeId,
      completed = false,
      items,
      title,
      collapsed = false,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const activeIndex = items.findIndex(i => i.id === activeId);
    const itemsWithStatus = items.map((item, index) => {
      let status: StepStatus = 'pending';
      if (completed || index < activeIndex) status = 'done';
      else if (index === activeIndex && item.id === activeId) status = 'active';
      return { ...item, status };
    });

    return (
      <div
        ref={ref}
        className={cx(
          'bg-primary h-full rounded-[24px] flex flex-col relative overflow-hidden',
          collapsed ? 'w-[84px] pl-4 pr-4 pt-8 pb-8' : 'w-[336px] p-8',
          className,
        )}
        {...props}
      >
        {/* Logo container */}
        <div className="mb-8">
          {collapsed ? (
            // Tablet (stepper collapsed)
            <CompactLogo className="h-10 w-auto block shrink-0" />
          ) : (
            // Desktop – horizontal logo
            <HorizontalLogo
              className="w-[160px] h-auto block shrink-0"
              preserveAspectRatio="xMidYMid meet"
            />
          )}
        </div>

        {!collapsed && (
          <span className="mb-8 font-bold text-base text-white">{title}</span>
        )}

        {itemsWithStatus.map((item, index) => (
          <SideStepperItem
            key={`${index}_${item.id}`}
            icon={item.icon}
            text={item.text}
            status={item.status}
            showSeparator={index !== itemsWithStatus.length - 1}
            collapsed={collapsed}
          />
        ))}

        <div className="sticky bottom-8 left-8">{children}</div>
      </div>
    );
  },
);

SideStepper.displayName = 'SideStepper';
